import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { R } from '../ProfileResources.resx';
import { useAtom, useSetAtom } from 'jotai';
import { Utils } from '@/shared/Utils';
import { MyInfoModal } from './MyInfoModal';
import { selfRequestAtom, selfResponseAtom } from './myInfoAtoms';
import { ISelfResponse } from '@/shared/interfaces';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import editIcon from '../../../../wwwroot/images/icons/edit.svg';
import { useHydrateAtoms } from 'jotai/utils';

export const MyInfoApp = (
    containerQuery: string,
    selfResponse: ISelfResponse,
) => {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    const queryClient = new QueryClient();
    root.render(
        <QueryClientProvider client={queryClient}>
            <MyInfo bootstrappedSelfResponse={selfResponse} />
        </QueryClientProvider>,
    );
};

type MyInfoProps = {
    bootstrappedSelfResponse: ISelfResponse;
};

const MyInfo = ({ bootstrappedSelfResponse }: MyInfoProps) => {
    useHydrateAtoms([[selfResponseAtom, bootstrappedSelfResponse]]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const setSelfResponseAtom = useSetAtom(selfResponseAtom);
    const [self] = useAtom(selfRequestAtom);

    return (
        self && (
            <>
                <div className="flex flex-wrap gap-6">
                    <div className="shrink grow basis-64">
                        <label className="mb-1 text-sm font-normal leading-tight text-grey-700">
                            {R.Name}
                        </label>
                        <div className="mb-2 break-all text-lg font-bold leading-normal text-grey-900">
                            {self.Name}
                        </div>
                        <label className="mb-1 text-sm font-normal leading-tight text-grey-700">
                            {R.CprNumber}
                        </label>
                        <div className="text-lg font-bold leading-normal text-grey-900">
                            {Utils.formatCpr(self.CPR)}
                        </div>
                    </div>

                    <button
                        aria-label={R.EditMyInformationLabelA11y}
                        className="relative shrink grow basis-64 cursor-pointer rounded-2xl border border-grey-300 p-4 text-left hover:bg-grey-100"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <label className="pointer-events-none mb-1 text-sm font-normal leading-tight text-grey-700">
                            {R.Email}
                        </label>
                        <div className="mb-2 break-all text-lg font-bold leading-normal text-grey-900">
                            {self.Email}
                        </div>
                        <label className="pointer-events-none mb-1 text-sm font-normal leading-tight text-grey-700">
                            {R.Phone}
                        </label>
                        <div className="text-lg font-bold leading-normal text-grey-900">
                            {self.Mobile}
                        </div>
                        <img
                            className="absolute right-4 top-4"
                            width="24"
                            height="24"
                            src={editIcon}
                            alt=""
                            aria-hidden="true"
                        />
                    </button>
                </div>
                {isModalOpen && (
                    <MyInfoModal
                        selfOnOpen={self}
                        onClose={(selfResponse) => {
                            if (selfResponse) setSelfResponseAtom(selfResponse);
                            setIsModalOpen(false);
                        }}
                    ></MyInfoModal>
                )}
            </>
        )
    );
};
